import React, { useCallback, useEffect, useState } from 'react';
import { API_URL } from './api';
import StripeConnectOverview from './StripeConnectOverview';
import UberDirectOverview from './UberDirectOverview';
import StoreCommerceOverview from './StoreCommerceOverview';
import StoreAfterSalesOverview from './StoreAfterSalesOverview';

const FALLBACK_IMAGE_MODEL_OPTIONS = ['gpt-image-1-mini', 'gpt-image-1'];
const DEFAULT_CLOSURE_TYPE = 'Z_DAILY';
const DEFAULT_ARCHIVE_TYPE = 'manual';
const DEFAULT_ARCHIVE_FORMAT = 'json';
const EMPTY_CONTROL_PROFILE = {
  complianceSoftwareName: 'Beborne',
  complianceSoftwareVersion: '',
  compliancePublisherName: '',
  compliancePublisherAddress: '',
  compliancePublisherSiren: '',
  complianceAttestationReference: '',
  complianceAttestationDate: '',
  complianceContactEmail: '',
};
const DEFAULT_MYPOS_FEE_RATES = {
  domesticConsumerEea: 1.69,
  amex: 2.49,
  default: 2.89,
};
const DEFAULT_ACCOUNT_FEATURES = {
  borneEnabled: true,
  caisseEnabled: true,
  padEnabled: true,
  kitchenScreenEnabled: true,
  fidScreenEnabled: true,
  beguestEnabled: false,
  clickCollectEnabled: true,
  reservationEnabled: true,
  customerChatbotEnabled: false,
  customerChatbotDailyLimit: 20,
  customerChatbotIpDailyLimit: 80,
  borneLimit: 1,
  caisseLimit: 1,
  padLimit: 2,
  kitchenScreenLimit: 1,
  fidScreenLimit: 1,
  beguestLimit: 0,
};

const DEFAULT_SERVICE_ACCESS = {
  bestock: false,
  bequality: false,
  becost: false,
  betime: false,
};

const DEFAULT_BEGUEST_SETTINGS = {
  adminCode: '1993',
  supportPhone: '',
  restaurantLogoMedia: '',
};

const ACCOUNT_FEATURE_ROWS = [
  ['borneEnabled', 'borneLimit', 'Bornes client'],
  ['caisseEnabled', 'caisseLimit', 'Caisses centrales'],
  ['padEnabled', 'padLimit', 'PAD serveur'],
  ['kitchenScreenEnabled', 'kitchenScreenLimit', 'Écrans cuisine'],
  ['fidScreenEnabled', 'fidScreenLimit', 'Écrans FID'],
  ['beguestEnabled', 'beguestLimit', 'BEGuest e-paper'],
  ['clickCollectEnabled', null, 'Click & Collect'],
  ['reservationEnabled', null, 'Réservations web'],
  ['customerChatbotEnabled', null, 'Chatbot client QR'],
];

const SERVICE_ACCESS_ROWS = [
  ['bestock', 'BESTOCK', 'Stock, inventaires et achats'],
  ['bequality', 'BEQUALITY', 'Hygiène, contrôles et traçabilité'],
  ['becost', 'BECOST', 'Coûts, marges et documents'],
  ['betime', 'BETIME', 'Planning, salariés et pointage'],
];

function createFeaturesDraft(features = {}) {
  return {
    ...DEFAULT_ACCOUNT_FEATURES,
    ...(features || {}),
    borneEnabled: features?.borneEnabled !== false,
    caisseEnabled: !!features?.caisseEnabled,
    padEnabled: features?.padEnabled !== false,
    kitchenScreenEnabled: features?.kitchenScreenEnabled !== false,
    fidScreenEnabled: features?.fidScreenEnabled !== false,
    beguestEnabled: !!features?.beguestEnabled,
    clickCollectEnabled: features?.clickCollectEnabled !== false,
    reservationEnabled: features?.reservationEnabled !== false,
    customerChatbotEnabled: !!features?.customerChatbotEnabled,
    customerChatbotDailyLimit: Number(features?.customerChatbotDailyLimit ?? DEFAULT_ACCOUNT_FEATURES.customerChatbotDailyLimit),
    customerChatbotIpDailyLimit: Number(features?.customerChatbotIpDailyLimit ?? DEFAULT_ACCOUNT_FEATURES.customerChatbotIpDailyLimit),
    borneLimit: Number(features?.borneLimit ?? DEFAULT_ACCOUNT_FEATURES.borneLimit),
    caisseLimit: Number(features?.caisseLimit ?? DEFAULT_ACCOUNT_FEATURES.caisseLimit),
    padLimit: Number(features?.padLimit ?? DEFAULT_ACCOUNT_FEATURES.padLimit),
    kitchenScreenLimit: Number(features?.kitchenScreenLimit ?? DEFAULT_ACCOUNT_FEATURES.kitchenScreenLimit),
    fidScreenLimit: Number(features?.fidScreenLimit ?? DEFAULT_ACCOUNT_FEATURES.fidScreenLimit),
    beguestLimit: Number(features?.beguestLimit ?? DEFAULT_ACCOUNT_FEATURES.beguestLimit),
  };
}

function createServiceAccessDraft(services = {}) {
  return SERVICE_ACCESS_ROWS.reduce((result, [key]) => {
    const value = services?.[key];
    result[key] = typeof value === 'object' ? !!value.enabled : !!value;
    return result;
  }, { ...DEFAULT_SERVICE_ACCESS });
}

function createDefaultControlFilters() {
  return {
    accountId: '',
    dateFrom: toDateInputValue(new Date(new Date().getFullYear(), 0, 1)),
    dateTo: toDateInputValue(new Date()),
  };
}

function createDefaultInvoiceFilters() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  return {
    accountId: '',
    dateFrom: toDateInputValue(start),
    dateTo: toDateInputValue(end),
  };
}

function createBillingDraft(account = {}) {
  const billing = account.billing || {};
  return {
    legalName: billing.legalName || '',
    tradeName: billing.tradeName || account.name || '',
    billingAddressLine1: billing.billingAddressLine1 || '',
    billingAddressLine2: billing.billingAddressLine2 || '',
    billingPostalCode: billing.billingPostalCode || '',
    billingCity: billing.billingCity || '',
    siret: billing.siret || '',
    vatNumber: billing.vatNumber || '',
    nafCode: billing.nafCode || '',
    subscriptionFeeHt: billing.subscriptionFeeHt ?? 0,
    subscriptionDiscountPct: billing.subscriptionDiscountPct ?? 0,
    myposFeeRates: {
      ...DEFAULT_MYPOS_FEE_RATES,
      ...(billing.myposFeeRates || {}),
    },
  };
}

function createBeguestSettingsDraft(settings = {}) {
  const digits = String(settings?.adminCode ?? DEFAULT_BEGUEST_SETTINGS.adminCode).replace(/\D+/g, '').slice(0, 8);
  return {
    adminCode: digits.length >= 4 ? digits : DEFAULT_BEGUEST_SETTINGS.adminCode,
    supportPhone: String(settings?.supportPhone ?? DEFAULT_BEGUEST_SETTINGS.supportPhone).trim(),
    restaurantLogoMedia: String(settings?.restaurantLogoMedia ?? DEFAULT_BEGUEST_SETTINGS.restaurantLogoMedia).trim(),
  };
}

function createUberDirectDraft(settings = {}) {
  return {
    integrationMode: settings.integrationMode === 'partner' ? 'partner' : 'legacy',
    enabled: !!settings.enabled,
    environment: settings.environment === 'production' ? 'production' : 'sandbox',
    customerId: settings.customerId || '',
    clientId: settings.clientId || '',
    clientSecret: settings.clientSecret || '',
    clientSecretConfigured: !!settings.clientSecretConfigured,
    webhookSigningKey: settings.webhookSigningKey || '',
    webhookSigningKeyConfigured: !!settings.webhookSigningKeyConfigured,
    pickupName: settings.pickupName || '',
    pickupPhone: settings.pickupPhone || '',
    pickupAddressLine1: settings.pickupAddressLine1 || '',
    pickupAddressLine2: settings.pickupAddressLine2 || '',
    pickupCity: settings.pickupCity || '',
    pickupPostalCode: settings.pickupPostalCode || '',
    pickupCountry: settings.pickupCountry || 'FR',
    pickupLatitude: settings.pickupLatitude ?? '',
    pickupLongitude: settings.pickupLongitude ?? '',
    configured: !!settings.configured,
    missingFields: Array.isArray(settings.missingFields) ? settings.missingFields : [],
  };
}

function normalizeBeguestList(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  return [...new Set(value.map((item) => String(item || '').trim().toUpperCase()).filter(Boolean))];
}

function formatCurrency(value) {
  return `${Number(value || 0).toFixed(2)} EUR`;
}

function formatDelta(value) {
  const numeric = Number(value || 0);
  const prefix = numeric > 0 ? '+' : '';
  return `${prefix}${numeric.toFixed(2)}%`;
}

function formatDateTime(value) {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return date.toLocaleString('fr-FR');
}

function getBeguestSyncMeta(device) {
  const state = String(device?.syncState || 'idle').trim().toLowerCase();
  const reason = String(device?.syncStateReason || '').trim().toLowerCase();
  if (state === 'pending') {
    if (reason === 'waiting_for_first_checkin') {
      return {
        label: 'Jamais vu',
        className: 'is-pending',
        detail: 'Le terminal est assigne mais n a encore envoye aucun check-in.',
      };
    }
    if (reason === 'device_seen_pending_sync') {
      return {
        label: 'MAJ vue',
        className: 'is-pending',
        detail: 'Le terminal a ete vu apres la MAJ mais n a pas encore redemande son menu.',
      };
    }
    if (reason === 'device_requested_sync') {
      return {
        label: 'Sync demandee',
        className: 'is-pending',
        detail: 'Le terminal a demande une synchronisation et attend encore l application finale.',
      };
    }
    return {
      label: 'MAJ en attente',
      className: 'is-pending',
      detail: 'Une nouvelle version menu est disponible et attend le prochain check-in utile.',
    };
  }
  if (state === 'applied') {
    return {
      label: 'À jour',
      className: 'is-applied',
      detail: 'Le terminal confirme la meme version que la cible restaurant.',
    };
  }
  return {
    label: 'Sans retour',
    className: 'is-idle',
    detail: 'Aucun etat exploitable remonte pour ce terminal.',
  };
}

function getFiscalChainStatus(chainReport) {
  if (!chainReport) {
    return {
      label: 'Verification auto...',
      className: 'master-neutral-text',
      description: 'Controle lance automatiquement au chargement de Master.',
    };
  }
  const checked = Number(chainReport.checked || 0);
  const issues = Array.isArray(chainReport.issues) ? chainReport.issues.length : 0;
  if (checked <= 0) {
    return {
      label: 'Aucune donnee',
      className: 'master-neutral-text',
      description: 'Aucun evenement fiscal a verifier pour les filtres selectionnes.',
    };
  }
  if (issues > 0 || chainReport.valid === false) {
    return {
      label: 'Anomalie',
      className: 'master-invalid-text',
      description: 'Une rupture ou une incoherence a ete detectee dans la chaine fiscale.',
    };
  }
  return {
    label: 'OK',
    className: 'master-valid-text',
    description: 'Chaine fiscale controlee automatiquement, aucune anomalie detectee.',
  };
}

function toDateInputValue(value = new Date()) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function buildFiscalQuery(filters = {}, extra = {}) {
  const params = new URLSearchParams();
  if (filters.accountId) {
    params.set('accountId', String(filters.accountId));
  }
  if (filters.dateFrom) {
    params.set('dateFrom', String(filters.dateFrom));
  }
  if (filters.dateTo) {
    params.set('dateTo', String(filters.dateTo));
  }
  Object.entries(extra || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      return;
    }
    params.set(key, String(value));
  });
  return params.toString();
}

function resolveFilename(contentDisposition, fallback = 'download.bin') {
  const match = String(contentDisposition || '').match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
  if (!match || !match[1]) {
    return fallback;
  }

  try {
    return decodeURIComponent(match[1].replace(/"/g, '').trim());
  } catch (error) {
    return match[1].replace(/"/g, '').trim() || fallback;
  }
}

function triggerDownload(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

function getBefidAppUrl() {
  if (typeof window === 'undefined') {
    return 'http://localhost:8095/master/login';
  }
  if (window.location.protocol === 'https:' || window.location.hostname === 'beborne.com') {
    return `${window.location.origin}/befid-app/master/login`;
  }
  return `${window.location.protocol}//${window.location.hostname}:8095/master/login`;
}

function MasterConsole() {
  const [token, setToken] = useState(localStorage.getItem('masterToken') || '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountPassword, setNewAccountPassword] = useState('');
  const [newAccountImageModel, setNewAccountImageModel] = useState(FALLBACK_IMAGE_MODEL_OPTIONS[0]);
  const [newAccountFeatures, setNewAccountFeatures] = useState(DEFAULT_ACCOUNT_FEATURES);
  const [newAccountServices, setNewAccountServices] = useState(DEFAULT_SERVICE_ACCESS);
  const [savingId, setSavingId] = useState(null);
  const [draftByAccount, setDraftByAccount] = useState({});
  const [backups, setBackups] = useState([]);
  const [controlFilters, setControlFilters] = useState(() => createDefaultControlFilters());
  const [invoiceFilters, setInvoiceFilters] = useState(() => createDefaultInvoiceFilters());
  const [selfBillingInvoices, setSelfBillingInvoices] = useState([]);
  const [controlProfile, setControlProfile] = useState(EMPTY_CONTROL_PROFILE);
  const [chainReport, setChainReport] = useState(null);
  const [closuresPreview, setClosuresPreview] = useState([]);
  const [archivesPreview, setArchivesPreview] = useState([]);
  const [auditPreview, setAuditPreview] = useState([]);
  const [closureType, setClosureType] = useState(DEFAULT_CLOSURE_TYPE);
  const [archiveType, setArchiveType] = useState(DEFAULT_ARCHIVE_TYPE);
  const [archiveFormat, setArchiveFormat] = useState(DEFAULT_ARCHIVE_FORMAT);
  const [autocertDocuments, setAutocertDocuments] = useState([]);
  const [beloraBeguestCatalog, setBeloraBeguestCatalog] = useState([]);
  const [beloraManagerReachable, setBeloraManagerReachable] = useState(false);
  const [beloraManagerBaseUrl, setBeloraManagerBaseUrl] = useState('');
  const [beguestMenuPreview, setBeguestMenuPreview] = useState(null);
  const fiscalChainStatus = getFiscalChainStatus(chainReport);

  const authHeaders = (masterToken, includeJson = false) => {
    const headers = {
      Authorization: `Bearer ${masterToken}`,
    };
    if (includeJson) {
      headers['Content-Type'] = 'application/json';
    }
    return headers;
  };

  const fetchOverview = useCallback(async (masterToken) => {
    const response = await fetch(`${API_URL}/api/master/overview`, {
      headers: authHeaders(masterToken),
    });
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload?.error || 'Session master expiree.');
    }
    setOverview(payload);
    const availableModels = Array.isArray(payload.imageGenerationModelOptions) && payload.imageGenerationModelOptions.length
      ? payload.imageGenerationModelOptions
      : FALLBACK_IMAGE_MODEL_OPTIONS;
    setDraftByAccount((previous) => {
      const next = {};
      (payload.accounts || []).forEach((account) => {
        next[account.id] = {
          name: previous[account.id]?.name ?? account.name,
          password: '',
          isActive: previous[account.id]?.isActive ?? (account.isActive !== false),
          imageGenerationModel:
            previous[account.id]?.imageGenerationModel
            ?? account.imageGenerationModel
            ?? availableModels[0],
          features: previous[account.id]?.features ?? createFeaturesDraft(account.features),
          serviceAccess: previous[account.id]?.serviceAccess ?? createServiceAccessDraft(account.services),
          billing: previous[account.id]?.billing ?? createBillingDraft(account),
          beguestSettings: previous[account.id]?.beguestSettings ?? createBeguestSettingsDraft(account.beguestSettings),
          uberDirect: previous[account.id]?.uberDirect ?? createUberDirectDraft(account.uberDirect),
          beguestDevices: previous[account.id]?.beguestDevices ?? normalizeBeguestList(account.beguestDevices),
          beguestInput: previous[account.id]?.beguestInput ?? '',
        };
      });
      return next;
    });
    setNewAccountImageModel((previous) => {
      if (availableModels.includes(previous)) {
        return previous;
      }
      return payload.defaultImageGenerationModel || availableModels[0];
    });
  }, []);

  const fetchBeloraBeguestCatalog = useCallback(async (masterToken) => {
    const response = await fetch(`${API_URL}/api/master/belora/beguests`, {
      headers: authHeaders(masterToken),
    });
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload?.error || 'Chargement inventaire BEGuest impossible.');
    }
    setBeloraManagerReachable(payload?.managerReachable === true);
    setBeloraManagerBaseUrl(String(payload?.managerBaseUrl || ''));
    setBeloraBeguestCatalog(Array.isArray(payload?.devices) ? payload.devices : []);
  }, []);

  const refreshBeloraBeguestCatalog = useCallback(async (masterToken) => {
    const response = await fetch(`${API_URL}/api/master/belora/beguests/refresh`, {
      method: 'POST',
      headers: authHeaders(masterToken),
    });
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload?.error || 'Rafraichissement BEGuest impossible.');
    }
    setBeloraManagerReachable(payload?.managerReachable === true);
    setBeloraManagerBaseUrl(String(payload?.managerBaseUrl || ''));
    setBeloraBeguestCatalog(Array.isArray(payload?.devices) ? payload.devices : []);
    return payload?.refresh || { skipped: false, count: 0 };
  }, []);

  const fetchBeguestMenuPreview = useCallback(async (masterToken, deviceName) => {
    const response = await fetch(`${API_URL}/api/master/belora/beguests/${encodeURIComponent(deviceName)}/menu`, {
      headers: authHeaders(masterToken),
    });
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload?.error || 'Apercu menu BEGuest impossible.');
    }
    setBeguestMenuPreview(payload);
  }, []);

  const fetchBackups = useCallback(async (masterToken) => {
    const response = await fetch(`${API_URL}/api/master/system/backups`, {
      headers: authHeaders(masterToken),
    });
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload?.error || 'Chargement des sauvegardes impossible.');
    }
    setBackups(payload.backups || []);
  }, []);

  const fetchControlProfile = useCallback(async (masterToken) => {
    const response = await fetch(`${API_URL}/api/master/fiscal/control-profile`, {
      headers: authHeaders(masterToken),
    });
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload?.error || 'Chargement du profil controle impossible.');
    }
    setControlProfile({
      ...EMPTY_CONTROL_PROFILE,
      ...(payload || {}),
    });
  }, []);

  const fetchAutocertDocuments = useCallback(async (masterToken) => {
    const response = await fetch(`${API_URL}/api/master/fiscal/autocert-documents`, {
      headers: authHeaders(masterToken),
    });
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload?.error || 'Chargement des documents autocertification impossible.');
    }
    setAutocertDocuments(Array.isArray(payload?.documents) ? payload.documents : []);
  }, []);

  const fetchFiscalPreview = useCallback(async (masterToken, filters) => {
    const activeFilters = filters || {};
    const chainParams = new URLSearchParams();
    if (activeFilters.accountId) {
      chainParams.set('accountId', String(activeFilters.accountId));
    }

    const listQuery = buildFiscalQuery(activeFilters, { limit: 8 });
    const [chainResponse, closuresResponse, archivesResponse, auditResponse] = await Promise.all([
      fetch(`${API_URL}/api/master/fiscal/chain/verify?${chainParams.toString()}`, {
        headers: authHeaders(masterToken),
      }),
      fetch(`${API_URL}/api/master/fiscal/closures?${listQuery}`, {
        headers: authHeaders(masterToken),
      }),
      fetch(`${API_URL}/api/master/fiscal/archives?${listQuery}`, {
        headers: authHeaders(masterToken),
      }),
      fetch(`${API_URL}/api/master/fiscal/audit-logs?${listQuery}`, {
        headers: authHeaders(masterToken),
      }),
    ]);

    const chainPayload = await chainResponse.json();
    if (!chainResponse.ok) {
      throw new Error(chainPayload?.error || 'Verification de chaine impossible.');
    }

    const closuresPayload = await closuresResponse.json();
    if (!closuresResponse.ok) {
      throw new Error(closuresPayload?.error || 'Chargement des clotures impossible.');
    }

    const archivesPayload = await archivesResponse.json();
    if (!archivesResponse.ok) {
      throw new Error(archivesPayload?.error || 'Chargement des archives impossible.');
    }

    const auditPayload = await auditResponse.json();
    if (!auditResponse.ok) {
      throw new Error(auditPayload?.error || 'Chargement des audits impossible.');
    }

    setChainReport(chainPayload?.verification || null);
    setClosuresPreview(closuresPayload?.closures || []);
    setArchivesPreview(archivesPayload?.archives || []);
    setAuditPreview(auditPayload?.logs || []);
  }, []);

  const fetchSelfBillingInvoices = useCallback(async (masterToken, filters) => {
    const query = buildFiscalQuery(filters || {}, { limit: 20 });
    const response = await fetch(`${API_URL}/api/master/self-billing/invoices?${query}`, {
      headers: authHeaders(masterToken),
    });
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload?.error || 'Chargement autofactures impossible.');
    }
    setSelfBillingInvoices(Array.isArray(payload?.invoices) ? payload.invoices : []);
  }, []);

  const downloadWithAuth = async (path, fallbackName) => {
    const response = await fetch(path, {
      headers: authHeaders(token),
    });

    if (!response.ok) {
      let errorMessage = 'Telechargement impossible.';
      try {
        const payload = await response.json();
        errorMessage = payload?.error || errorMessage;
      } catch (parseError) {
        // ignore json parse issues
      }
      throw new Error(errorMessage);
    }

    const blob = await response.blob();
    const filename = resolveFilename(response.headers.get('content-disposition'), fallbackName);
    triggerDownload(blob, filename);
  };

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    let isActive = true;
    const load = async () => {
      try {
        await fetchOverview(token);
        await fetchBackups(token);
        await fetchControlProfile(token);
        await fetchAutocertDocuments(token);
        await fetchBeloraBeguestCatalog(token);
        await fetchFiscalPreview(token, createDefaultControlFilters());
        await fetchSelfBillingInvoices(token, createDefaultInvoiceFilters());
        if (isActive) {
          setError('');
        }
      } catch (err) {
        if (!isActive) {
          return;
        }
        setError(err.message || 'Chargement impossible');
        setToken('');
        localStorage.removeItem('masterToken');
      }
    };

    load();
    return () => {
      isActive = false;
    };
  }, [fetchAutocertDocuments, fetchBackups, fetchBeloraBeguestCatalog, fetchControlProfile, fetchFiscalPreview, fetchOverview, fetchSelfBillingInvoices, token]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/master/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const payload = await response.json();
      if (!response.ok || !payload?.token) {
        throw new Error(payload?.message || 'Connexion refusee');
      }
      localStorage.setItem('masterToken', payload.token);
      setToken(payload.token);
    } catch (err) {
      setError(err.message || 'Connexion impossible');
    } finally {
      setLoading(false);
    }
  };

  const handleManualBackup = async () => {
    setSavingId('manual-backup');
    setError('');
    setNotice('');
    try {
      const response = await fetch(`${API_URL}/api/master/system/backup`, {
        method: 'POST',
        headers: authHeaders(token, true),
        body: JSON.stringify({ reason: 'manual-master' }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Sauvegarde impossible');
      }
      await fetchBackups(token);
      setNotice('Sauvegarde SQLite creee.');
    } catch (err) {
      setError(err.message || 'Sauvegarde impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleManualFullBackup = async () => {
    setSavingId('manual-full-backup');
    setError('');
    setNotice('');
    try {
      const response = await fetch(`${API_URL}/api/master/system/backup/full`, {
        method: 'POST',
        headers: authHeaders(token, true),
        body: JSON.stringify({ reason: 'manual-master-full' }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Sauvegarde complete impossible');
      }
      await fetchBackups(token);
      setNotice('Sauvegarde complete creee.');
    } catch (err) {
      setError(err.message || 'Sauvegarde complete impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleRestoreBackup = async (backup) => {
    if (!backup?.filename) {
      return;
    }

    const confirmed = window.confirm(
      `Restaurer ${backup.filename} ?\n\nLe backend va redemarrer et la borne reviendra a l etat de cette sauvegarde.`
    );
    if (!confirmed) {
      return;
    }

    setSavingId(`restore:${backup.filename}`);
    setError('');
    setNotice('');
    try {
      const response = await fetch(
        `${API_URL}/api/master/system/backups/${encodeURIComponent(backup.filename)}/restore`,
        {
          method: 'POST',
          headers: authHeaders(token),
        }
      );
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Restauration impossible');
      }
      window.alert(payload.message || 'Restauration lancee. Le backend redemarre.');
    } catch (err) {
      setError(err.message || 'Restauration impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleCreateAccount = async (event) => {
    event.preventDefault();
    if (!newAccountName.trim() || !newAccountPassword.trim()) {
      return;
    }
    setSavingId('new');
    setError('');
    setNotice('');
    try {
      const response = await fetch(`${API_URL}/api/master/accounts`, {
        method: 'POST',
        headers: authHeaders(token, true),
        body: JSON.stringify({
          name: newAccountName.trim(),
          password: newAccountPassword.trim(),
          imageGenerationModel: newAccountImageModel,
          features: createFeaturesDraft(newAccountFeatures),
          serviceAccess: createServiceAccessDraft(newAccountServices),
          billing: createBillingDraft({ name: newAccountName.trim() }),
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Creation impossible');
      }
      setNewAccountName('');
      setNewAccountPassword('');
      setNewAccountFeatures(DEFAULT_ACCOUNT_FEATURES);
      setNewAccountServices(DEFAULT_SERVICE_ACCESS);
      setNewAccountImageModel(
        overview?.defaultImageGenerationModel
          || (overview?.imageGenerationModelOptions || FALLBACK_IMAGE_MODEL_OPTIONS)[0]
      );
      await fetchOverview(token);
      setNotice('Compte borne cree.');
    } catch (err) {
      setError(err.message || 'Creation impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleUpdateAccount = async (accountId) => {
    const draft = draftByAccount[accountId];
    if (!draft) {
      return;
    }
    setSavingId(accountId);
    setError('');
    setNotice('');
    try {
      const response = await fetch(`${API_URL}/api/master/accounts/${accountId}`, {
        method: 'PUT',
        headers: authHeaders(token, true),
        body: JSON.stringify({
          name: draft.name,
          password: draft.password || undefined,
          isActive: !!draft.isActive,
          imageGenerationModel: draft.imageGenerationModel,
          features: createFeaturesDraft(draft.features),
          serviceAccess: createServiceAccessDraft(draft.serviceAccess),
          billing: draft.billing,
          beguestSettings: createBeguestSettingsDraft(draft.beguestSettings),
          beguestDevices: normalizeBeguestList(draft.beguestDevices),
          uberDirect: createUberDirectDraft(draft.uberDirect),
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Mise a jour impossible');
      }
      setDraftByAccount((previous) => {
        const next = { ...previous };
        delete next[accountId];
        return next;
      });
      await fetchOverview(token);
      await fetchBeloraBeguestCatalog(token);
      setNotice('Compte borne mis a jour.');
    } catch (err) {
      setError(err.message || 'Mise a jour impossible');
    } finally {
      setSavingId(null);
    }
  };

  const addBeguestToAccountDraft = (accountId) => {
    const draft = draftByAccount[accountId];
    if (!draft) {
      return;
    }
    const candidate = String(draft.beguestInput || '').trim().toUpperCase();
    if (!candidate) {
      return;
    }
    setDraftByAccount((previous) => ({
      ...previous,
      [accountId]: {
        ...draft,
        beguestDevices: normalizeBeguestList([...(draft.beguestDevices || []), candidate]),
        beguestInput: '',
      },
    }));
  };

  const removeBeguestFromAccountDraft = (accountId, deviceName) => {
    const draft = draftByAccount[accountId];
    if (!draft) {
      return;
    }
    setDraftByAccount((previous) => ({
      ...previous,
      [accountId]: {
        ...draft,
        beguestDevices: normalizeBeguestList((draft.beguestDevices || []).filter((item) => item !== deviceName)),
      },
    }));
  };

  const handleSyncTenantDatabase = async (accountId) => {
    setSavingId(`tenant-db:${accountId}`);
    setError('');
    setNotice('');
    try {
      const response = await fetch(`${API_URL}/api/master/accounts/${accountId}/tenant-database/sync`, {
        method: 'POST',
        headers: authHeaders(token, true),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Synchronisation BDD restaurant impossible');
      }
      await fetchOverview(token);
      await fetchBeloraBeguestCatalog(token);
      const copiedTotal = Number(payload?.sync?.copiedTotal || 0);
      setNotice(`BDD restaurant synchronisee (${copiedTotal} lignes copiees).`);
    } catch (err) {
      setError(err.message || 'Synchronisation BDD restaurant impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleRemoveIp = async (accountId, ip) => {
    setSavingId(`${accountId}:${ip}`);
    setError('');
    setNotice('');
    try {
      const response = await fetch(`${API_URL}/api/master/accounts/${accountId}/ips/remove`, {
        method: 'POST',
        headers: authHeaders(token, true),
        body: JSON.stringify({ ip }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Suppression IP impossible');
      }
      await fetchOverview(token);
      await fetchBeloraBeguestCatalog(token);
      setNotice('IP retiree.');
    } catch (err) {
      setError(err.message || 'Suppression IP impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleDisconnectAccount = async (accountId) => {
    setSavingId(`disconnect:${accountId}`);
    setError('');
    setNotice('');
    try {
      const response = await fetch(`${API_URL}/api/master/accounts/${accountId}`, {
        method: 'PUT',
        headers: authHeaders(token, true),
        body: JSON.stringify({ disconnectAll: true }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Deconnexion impossible');
      }
      await fetchOverview(token);
      await fetchBeloraBeguestCatalog(token);
      setNotice('Toutes les sessions borne ont ete deconnectees pour ce compte.');
    } catch (err) {
      setError(err.message || 'Deconnexion impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleRefreshMasterData = async () => {
    setSavingId('refresh-all');
    setError('');
    setNotice('');
    try {
      await fetchOverview(token);
      await fetchBeloraBeguestCatalog(token);
      await fetchBackups(token);
      await fetchControlProfile(token);
      await fetchAutocertDocuments(token);
      await fetchFiscalPreview(token, controlFilters);
      await fetchSelfBillingInvoices(token, invoiceFilters);
      setNotice('Console master rafraichie.');
    } catch (err) {
      setError(err.message || 'Rafraichissement impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleRefreshInvoices = async () => {
    setSavingId('refresh-invoices');
    setError('');
    setNotice('');
    try {
      await fetchSelfBillingInvoices(token, invoiceFilters);
      setNotice('Autofactures rafraichies.');
    } catch (err) {
      setError(err.message || 'Rafraichissement autofactures impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleGenerateSelfBillingInvoice = async () => {
    if (!invoiceFilters.accountId) {
      setError('Choisis un compte borne avant de generer une autofacture.');
      return;
    }
    setSavingId('generate-self-billing');
    setError('');
    setNotice('');
    try {
      const response = await fetch(`${API_URL}/api/master/self-billing/invoices`, {
        method: 'POST',
        headers: authHeaders(token, true),
        body: JSON.stringify({
          accountId: Number(invoiceFilters.accountId),
          periodStart: invoiceFilters.dateFrom,
          periodEnd: invoiceFilters.dateTo,
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Generation autofacture impossible');
      }
      await fetchSelfBillingInvoices(token, invoiceFilters);
      setNotice(`Autofacture ${payload.invoiceNumber || ''} generee.`);
    } catch (err) {
      setError(err.message || 'Generation autofacture impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handlePreviewBeguestMenu = async (deviceName) => {
    setSavingId(`beguest-preview:${deviceName}`);
    setError('');
    setNotice('');
    try {
      await fetchBeguestMenuPreview(token, deviceName);
      setNotice(`Apercu menu charge pour ${deviceName}.`);
    } catch (err) {
      setError(err.message || 'Apercu menu BEGuest impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleRefreshBeguestInventory = async () => {
    setSavingId('beguest-refresh');
    setError('');
    setNotice('');
    try {
      const refresh = await refreshBeloraBeguestCatalog(token);
      if (refresh?.skipped) {
        setNotice('Rafraichissement deja en cours.');
      } else {
        setNotice(`Etat BEGuest recalcule (${Number(refresh?.count || 0)} appareil${Number(refresh?.count || 0) > 1 ? 's' : ''}).`);
      }
    } catch (err) {
      setError(err.message || 'Rafraichissement BEGuest impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleDownloadSelfBillingInvoice = async (invoiceId) => {
    setSavingId(`download-invoice-${invoiceId}`);
    setError('');
    setNotice('');
    try {
      await downloadWithAuth(
        `${API_URL}/api/master/self-billing/invoices/${invoiceId}/download`,
        `autofacture-${invoiceId}.pdf`
      );
      setNotice('Autofacture telechargee.');
    } catch (err) {
      setError(err.message || 'Telechargement autofacture impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleRefreshFiscalData = async () => {
    setSavingId('refresh-fiscal');
    setError('');
    setNotice('');
    try {
      await fetchFiscalPreview(token, controlFilters);
      setNotice('Donnees fiscales rafraichies.');
    } catch (err) {
      setError(err.message || 'Rafraichissement fiscal impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleVerifyChain = async () => {
    setSavingId('verify-chain');
    setError('');
    setNotice('');
    try {
      const params = new URLSearchParams();
      if (controlFilters.accountId) {
        params.set('accountId', String(controlFilters.accountId));
      }
      const response = await fetch(`${API_URL}/api/master/fiscal/chain/verify?${params.toString()}`, {
        headers: authHeaders(token),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Verification chaine impossible');
      }
      setChainReport(payload?.verification || null);
      const status = getFiscalChainStatus(payload?.verification || null);
      setNotice(`Verification de chaine terminee: ${status.label}.`);
    } catch (err) {
      setError(err.message || 'Verification chaine impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleDownloadControlPackage = async () => {
    setSavingId('download-control-package');
    setError('');
    setNotice('');
    try {
      const query = buildFiscalQuery(controlFilters);
      await downloadWithAuth(
        `${API_URL}/api/master/fiscal/control-package/download?${query}`,
        'dossier-controle.tar.gz'
      );
      setNotice('Dossier de controle telecharge.');
    } catch (err) {
      setError(err.message || 'Generation du dossier controle impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleExportFiscal = async (format) => {
    const exportKey = `export-${format}`;
    setSavingId(exportKey);
    setError('');
    setNotice('');
    try {
      const query = buildFiscalQuery(controlFilters, { format });
      const fallback = format === 'csv' ? 'fiscal-events.csv' : 'fiscal-events.json';
      await downloadWithAuth(`${API_URL}/api/master/fiscal/export?${query}`, fallback);
      setNotice(`Export fiscal ${format.toUpperCase()} telecharge.`);
    } catch (err) {
      setError(err.message || `Export fiscal ${format.toUpperCase()} impossible`);
    } finally {
      setSavingId(null);
    }
  };

  const handleCreateClosure = async () => {
    setSavingId('create-closure');
    setError('');
    setNotice('');
    try {
      const from = controlFilters.dateFrom || toDateInputValue(new Date());
      const to = controlFilters.dateTo || from;
      const response = await fetch(`${API_URL}/api/master/fiscal/closures`, {
        method: 'POST',
        headers: authHeaders(token, true),
        body: JSON.stringify({
          accountId: controlFilters.accountId ? Number(controlFilters.accountId) : null,
          closureType,
          periodStart: `${from}T00:00:00`,
          periodEnd: `${to}T23:59:59.999`,
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Creation de cloture impossible');
      }
      await fetchFiscalPreview(token, controlFilters);
      setNotice('Cloture fiscale creee.');
    } catch (err) {
      setError(err.message || 'Creation de cloture impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleCreateArchive = async () => {
    setSavingId('create-archive');
    setError('');
    setNotice('');
    try {
      const from = controlFilters.dateFrom || toDateInputValue(new Date());
      const to = controlFilters.dateTo || from;
      const response = await fetch(`${API_URL}/api/master/fiscal/archives`, {
        method: 'POST',
        headers: authHeaders(token, true),
        body: JSON.stringify({
          accountId: controlFilters.accountId ? Number(controlFilters.accountId) : null,
          archiveType,
          format: archiveFormat,
          periodStart: `${from}T00:00:00`,
          periodEnd: `${to}T23:59:59.999`,
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Creation d archive impossible');
      }
      await fetchFiscalPreview(token, controlFilters);
      setNotice('Archive fiscale creee.');
    } catch (err) {
      setError(err.message || 'Creation d archive impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleDownloadArchive = async (archiveId) => {
    setSavingId(`download-archive-${archiveId}`);
    setError('');
    setNotice('');
    try {
      await downloadWithAuth(
        `${API_URL}/api/master/fiscal/archives/${archiveId}/download`,
        `fiscal-archive-${archiveId}.dat`
      );
      setNotice('Archive fiscale telechargee.');
    } catch (err) {
      setError(err.message || 'Telechargement archive impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleDownloadAutocertDocument = async (docId, format) => {
    const key = `download-autocert-${docId}-${format}`;
    setSavingId(key);
    setError('');
    setNotice('');
    try {
      await downloadWithAuth(
        `${API_URL}/api/master/fiscal/autocert-documents/${encodeURIComponent(docId)}/${encodeURIComponent(format)}`,
        `${docId}.${format}`
      );
      setNotice(`Document ${docId.toUpperCase()} (${format.toUpperCase()}) telecharge.`);
    } catch (err) {
      setError(err.message || 'Telechargement document autocertification impossible');
    } finally {
      setSavingId(null);
    }
  };

  const handleSaveControlProfile = async () => {
    setSavingId('save-control-profile');
    setError('');
    setNotice('');
    try {
      const response = await fetch(`${API_URL}/api/master/fiscal/control-profile`, {
        method: 'PUT',
        headers: authHeaders(token, true),
        body: JSON.stringify(controlProfile),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Enregistrement profil controle impossible');
      }
      setControlProfile({
        ...EMPTY_CONTROL_PROFILE,
        ...(payload || {}),
      });
      setNotice('Profil controle fiscal enregistre.');
    } catch (err) {
      setError(err.message || 'Enregistrement profil controle impossible');
    } finally {
      setSavingId(null);
    }
  };

  const updateAccountBillingDraft = (accountId, field, value) => {
    setDraftByAccount((previous) => {
      const current = previous[accountId] || {};
      return {
        ...previous,
        [accountId]: {
          ...current,
          billing: {
            ...createBillingDraft({ id: accountId }),
            ...(current.billing || {}),
            [field]: value,
          },
        },
      };
    });
  };

  const updateAccountUberDirectDraft = (accountId, field, value) => {
    setDraftByAccount((previous) => {
      const current = previous[accountId] || {};
      return {
        ...previous,
        [accountId]: {
          ...current,
          uberDirect: { ...createUberDirectDraft(current.uberDirect), ...(current.uberDirect || {}), [field]: value },
        },
      };
    });
  };

  if (!token) {
    return (
      <div className="master-page">
        <div className="master-auth-shell">
          <div className="master-auth-card">
            <span className="master-kicker">Master</span>
            <h1>Console multi-bornes</h1>
            <p>Connecte-toi pour gerer les comptes borne, leurs IP memorisees et le dossier fiscal de controle.</p>
            <form onSubmit={handleLogin} className="master-auth-form">
              <label>Login master</label>
              <input value={username} onChange={(event) => setUsername(event.target.value)} required />
              <label>Mot de passe</label>
              <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} required />
              <button type="submit" className="master-primary-button" disabled={loading}>
                {loading ? 'Connexion...' : 'Se connecter'}
              </button>
            </form>
            {error ? <p className="master-error">{error}</p> : null}
          </div>
        </div>
      </div>
    );
  }

  const totals = overview?.totals || {};
  const accounts = overview?.accounts || [];
  const imageModelOptions = Array.isArray(overview?.imageGenerationModelOptions) && overview.imageGenerationModelOptions.length
    ? overview.imageGenerationModelOptions
    : FALLBACK_IMAGE_MODEL_OPTIONS;

  return (
    <div className="master-page">
      <div className="master-shell">
        <section className="master-hero">
          <div className="master-hero-copy">
            <span className="master-kicker">Master</span>
            <h1>Pilotage des comptes borne</h1>
            <p>Vue globale du reseau, creation de comptes, gestion des IP et dossier complet pour controle fiscal.</p>
          </div>
          <div className="master-actions">
            <a
              className="master-secondary-button master-action-link"
              href={getBefidAppUrl()}
              target="_blank"
              rel="noreferrer"
            >
              Ouvrir BEFID
            </a>
            <button
              type="button"
              className="master-secondary-button"
              onClick={handleRefreshMasterData}
              disabled={savingId === 'refresh-all'}
            >
              {savingId === 'refresh-all' ? 'Rafraichissement...' : 'Rafraichir'}
            </button>
            <button
              type="button"
              className="master-secondary-button"
              onClick={handleManualBackup}
              disabled={savingId === 'manual-backup'}
            >
              {savingId === 'manual-backup' ? 'Sauvegarde...' : 'Sauvegarde SQLite'}
            </button>
            <button
              type="button"
              className="master-secondary-button"
              onClick={handleManualFullBackup}
              disabled={savingId === 'manual-full-backup'}
            >
              {savingId === 'manual-full-backup' ? 'Sauvegarde complete...' : 'Sauvegarde complete'}
            </button>
            <button
              type="button"
              className="master-ghost-button"
              onClick={() => {
                localStorage.removeItem('masterToken');
                setToken('');
                setOverview(null);
                setBackups([]);
                setChainReport(null);
                setClosuresPreview([]);
                setArchivesPreview([]);
                setAuditPreview([]);
                setAutocertDocuments([]);
              }}
            >
              Deconnexion master
            </button>
          </div>
        </section>

        {error ? <p className="master-error master-error-inline">{error}</p> : null}
        {notice ? <p className="master-notice">{notice}</p> : null}

        <StripeConnectOverview token={token} />
        <UberDirectOverview token={token} />
        <StoreCommerceOverview token={token} />
        <StoreAfterSalesOverview token={token} />

        <section className="master-summary-grid">
          <article className="master-summary-card">
            <span>Comptes bornes</span>
            <strong>{totals.accountsTotal || 0}</strong>
            <small>{totals.accountsOnline || 0} connectes</small>
          </article>
          <article className="master-summary-card">
            <span>Commandes du jour</span>
            <strong>{totals.ordersToday || 0}</strong>
            <small>{formatCurrency(totals.revenueToday || 0)}</small>
          </article>
          <article className="master-summary-card">
            <span>Mois en cours</span>
            <strong>{totals.ordersMonth || 0}</strong>
            <small>{formatCurrency(totals.revenueMonth || 0)}</small>
          </article>
          <article className="master-summary-card">
            <span>Delta mois</span>
            <strong>{formatDelta(totals.deltaRevenuePct || 0)}</strong>
            <small>CA vs mois precedent</small>
          </article>
          <article className="master-summary-card">
            <span>SMS du mois</span>
            <strong>{totals.smsSentMonth || 0}</strong>
            <small>tous comptes confondus</small>
          </article>
          <article className="master-summary-card">
            <span>Fidelite</span>
            <strong>{totals.customersVerified || 0}</strong>
            <small>{totals.customersUnverified || 0} non valides</small>
          </article>
        </section>

        <section className="master-control-grid">
          <article className="master-control-card">
            <div>
              <span className="master-kicker">Controle fiscal</span>
              <h2>Dossier de controle</h2>
              <p className="master-empty-text">Gere la periode et telecharge les pieces justificatives depuis un seul bloc.</p>
            </div>

            <div className="master-control-form-grid">
              <label>Compte borne</label>
              <select
                value={controlFilters.accountId}
                onChange={(event) => setControlFilters((previous) => ({ ...previous, accountId: event.target.value }))}
              >
                <option value="">Tous les comptes</option>
                {accounts.map((account) => (
                  <option key={`control-account-${account.id}`} value={String(account.id)}>
                    {account.name}
                  </option>
                ))}
              </select>

              <label>Date debut</label>
              <input
                type="date"
                value={controlFilters.dateFrom}
                onChange={(event) => setControlFilters((previous) => ({ ...previous, dateFrom: event.target.value }))}
              />

              <label>Date fin</label>
              <input
                type="date"
                value={controlFilters.dateTo}
                onChange={(event) => setControlFilters((previous) => ({ ...previous, dateTo: event.target.value }))}
              />

              <label>Type cloture</label>
              <select value={closureType} onChange={(event) => setClosureType(event.target.value)}>
                <option value="Z_DAILY">Z_DAILY</option>
                <option value="Z_MONTHLY">Z_MONTHLY</option>
                <option value="Z_YEARLY">Z_YEARLY</option>
              </select>

              <label>Type archive</label>
              <select value={archiveType} onChange={(event) => setArchiveType(event.target.value)}>
                <option value="manual">manual</option>
                <option value="monthly">monthly</option>
                <option value="yearly">yearly</option>
              </select>

              <label>Format archive</label>
              <select value={archiveFormat} onChange={(event) => setArchiveFormat(event.target.value)}>
                <option value="json">json</option>
                <option value="csv">csv</option>
              </select>
            </div>

            <div className="master-control-actions">
              <button
                type="button"
                className="master-secondary-button"
                onClick={handleRefreshFiscalData}
                disabled={savingId === 'refresh-fiscal'}
              >
                {savingId === 'refresh-fiscal' ? 'Chargement...' : 'Rafraichir fiscal'}
              </button>
              <button
                type="button"
                className="master-secondary-button"
                onClick={handleVerifyChain}
                disabled={savingId === 'verify-chain'}
              >
                {savingId === 'verify-chain' ? 'Verification...' : 'Verifier maintenant'}
              </button>
              <button
                type="button"
                className="master-secondary-button"
                onClick={() => handleExportFiscal('csv')}
                disabled={savingId === 'export-csv'}
              >
                {savingId === 'export-csv' ? 'Export CSV...' : 'Export CSV'}
              </button>
              <button
                type="button"
                className="master-secondary-button"
                onClick={() => handleExportFiscal('json')}
                disabled={savingId === 'export-json'}
              >
                {savingId === 'export-json' ? 'Export JSON...' : 'Export JSON'}
              </button>
              <button
                type="button"
                className="master-primary-button"
                onClick={handleDownloadControlPackage}
                disabled={savingId === 'download-control-package'}
              >
                {savingId === 'download-control-package' ? 'Generation dossier...' : 'Telecharger dossier controle'}
              </button>
              <button
                type="button"
                className="master-secondary-button"
                onClick={handleCreateClosure}
                disabled={savingId === 'create-closure'}
              >
                {savingId === 'create-closure' ? 'Creation cloture...' : 'Creer cloture'}
              </button>
              <button
                type="button"
                className="master-secondary-button"
                onClick={handleCreateArchive}
                disabled={savingId === 'create-archive'}
              >
                {savingId === 'create-archive' ? 'Creation archive...' : 'Creer archive'}
              </button>
            </div>
          </article>

          <article className="master-control-card">
            <div>
              <span className="master-kicker">Attestation</span>
              <h2>Profil logiciel</h2>
              <p className="master-empty-text">Ces informations sont integrees automatiquement dans le dossier de controle.</p>
            </div>

            <div className="master-control-form-grid">
              <label>Nom logiciel</label>
              <input
                value={controlProfile.complianceSoftwareName || ''}
                onChange={(event) => setControlProfile((previous) => ({ ...previous, complianceSoftwareName: event.target.value }))}
              />

              <label>Version logiciel</label>
              <input
                value={controlProfile.complianceSoftwareVersion || ''}
                onChange={(event) => setControlProfile((previous) => ({ ...previous, complianceSoftwareVersion: event.target.value }))}
              />

              <label>Editeur</label>
              <input
                value={controlProfile.compliancePublisherName || ''}
                onChange={(event) => setControlProfile((previous) => ({ ...previous, compliancePublisherName: event.target.value }))}
              />

              <label>Adresse editeur</label>
              <input
                value={controlProfile.compliancePublisherAddress || ''}
                onChange={(event) => setControlProfile((previous) => ({ ...previous, compliancePublisherAddress: event.target.value }))}
              />

              <label>SIREN / SIRET</label>
              <input
                value={controlProfile.compliancePublisherSiren || ''}
                onChange={(event) => setControlProfile((previous) => ({ ...previous, compliancePublisherSiren: event.target.value }))}
              />

              <label>Reference attestation</label>
              <input
                value={controlProfile.complianceAttestationReference || ''}
                onChange={(event) => setControlProfile((previous) => ({ ...previous, complianceAttestationReference: event.target.value }))}
              />

              <label>Date attestation</label>
              <input
                value={controlProfile.complianceAttestationDate || ''}
                onChange={(event) => setControlProfile((previous) => ({ ...previous, complianceAttestationDate: event.target.value }))}
                placeholder="YYYY-MM-DD"
              />

              <label>Email contact</label>
              <input
                value={controlProfile.complianceContactEmail || ''}
                onChange={(event) => setControlProfile((previous) => ({ ...previous, complianceContactEmail: event.target.value }))}
              />
            </div>

            <div className="master-control-actions">
              <button
                type="button"
                className="master-primary-button"
                onClick={handleSaveControlProfile}
                disabled={savingId === 'save-control-profile'}
              >
                {savingId === 'save-control-profile' ? 'Enregistrement...' : 'Enregistrer profil'}
              </button>
            </div>
          </article>
        </section>

        <section className="master-control-grid">
          <article className="master-control-card">
            <div className="master-section-title">
              <span>Etat chaine fiscale</span>
              <strong className={fiscalChainStatus.className}>{fiscalChainStatus.label}</strong>
            </div>
            <p className="master-empty-text">{fiscalChainStatus.description}</p>
            <div className="master-list-row">
              <small>Evenements verifies: {Number(chainReport?.checked || 0)}</small>
              <small>Anomalies: {Number(chainReport?.issues?.length || 0)}</small>
            </div>
            <button
              type="button"
              className="master-secondary-button"
              onClick={handleVerifyChain}
              disabled={savingId === 'verify-chain'}
            >
              {savingId === 'verify-chain' ? 'Verification...' : 'Verifier maintenant'}
            </button>
            {(chainReport?.issues || []).slice(0, 4).map((issue, index) => (
              <div key={`chain-issue-${index}`} className="master-mini-item">
                <strong>Issue #{index + 1}</strong>
                <small>{String(issue?.message || issue?.reason || 'Anomalie detectee')}</small>
              </div>
            ))}
          </article>

          <article className="master-control-card">
            <div className="master-section-title">
              <span>Clotures recentes</span>
              <strong>{closuresPreview.length}</strong>
            </div>
            {closuresPreview.length ? closuresPreview.map((closure) => (
              <div key={`closure-${closure.id}`} className="master-mini-item">
                <strong>{closure.closureType}</strong>
                <small>{new Date(closure.generatedAt).toLocaleString('fr-FR')} · {closure.eventsCount || 0} events</small>
              </div>
            )) : <p className="master-empty-text">Aucune cloture pour les filtres selectionnes.</p>}
          </article>

          <article className="master-control-card">
            <div className="master-section-title">
              <span>Archives recentes</span>
              <strong>{archivesPreview.length}</strong>
            </div>
            {archivesPreview.length ? archivesPreview.map((archive) => (
              <div key={`archive-${archive.id}`} className="master-mini-item">
                <strong>{archive.archiveType} · {archive.format}</strong>
                <small>{new Date(archive.generatedAt).toLocaleString('fr-FR')}</small>
                <button
                  type="button"
                  className="master-secondary-button"
                  onClick={() => handleDownloadArchive(archive.id)}
                  disabled={savingId === `download-archive-${archive.id}`}
                >
                  {savingId === `download-archive-${archive.id}` ? 'Telechargement...' : 'Telecharger'}
                </button>
              </div>
            )) : <p className="master-empty-text">Aucune archive pour les filtres selectionnes.</p>}
          </article>

          <article className="master-control-card">
            <div className="master-section-title">
              <span>Audit recent</span>
              <strong>{auditPreview.length}</strong>
            </div>
            {auditPreview.length ? auditPreview.map((entry) => (
              <div key={`audit-${entry.id}`} className="master-mini-item">
                <strong>{entry.action}</strong>
                <small>{new Date(entry.occurredAt).toLocaleString('fr-FR')} · {entry.actorType}:{entry.actorId || '-'}</small>
              </div>
            )) : <p className="master-empty-text">Aucun log audit pour les filtres selectionnes.</p>}
          </article>

          <article className="master-control-card">
            <div className="master-section-title">
              <span>Autocertification</span>
              <strong>{autocertDocuments.length}</strong>
            </div>
            {autocertDocuments.length ? autocertDocuments.map((doc) => (
              <div key={`autocert-${doc.id}`} className="master-mini-item">
                <strong>{doc.label || doc.id}</strong>
                <small>Formats disponibles: {(doc.formats || []).join(', ').toUpperCase()}</small>
                <div className="master-control-actions">
                  {(doc.files?.pdf?.filename) ? (
                    <button
                      type="button"
                      className="master-secondary-button"
                      onClick={() => handleDownloadAutocertDocument(doc.id, 'pdf')}
                      disabled={savingId === `download-autocert-${doc.id}-pdf`}
                    >
                      {savingId === `download-autocert-${doc.id}-pdf` ? 'Telechargement PDF...' : 'Telecharger PDF'}
                    </button>
                  ) : null}
                  {(doc.files?.md?.filename) ? (
                    <button
                      type="button"
                      className="master-secondary-button"
                      onClick={() => handleDownloadAutocertDocument(doc.id, 'md')}
                      disabled={savingId === `download-autocert-${doc.id}-md`}
                    >
                      {savingId === `download-autocert-${doc.id}-md` ? 'Telechargement MD...' : 'Telecharger MD'}
                    </button>
                  ) : null}
                </div>
              </div>
            )) : <p className="master-empty-text">Aucun document d autocertification disponible.</p>}
          </article>
        </section>

        <section className="master-create-card master-invoice-panel">
          <div>
            <span className="master-kicker">Autofacturation</span>
            <h2>Factures restaurant vers BEKIOSK</h2>
            <p className="master-empty-text">
              Suivi des reversements historiques et rapprochements. Les paiements Stripe se consultent dans le tableau de bord des établissements.
            </p>
          </div>

          <div className="master-control-form-grid">
            <label>Restaurant</label>
            <select
              value={invoiceFilters.accountId}
              onChange={(event) => setInvoiceFilters((previous) => ({ ...previous, accountId: event.target.value }))}
            >
              <option value="">Choisir un compte</option>
              {accounts.map((account) => (
                <option key={`invoice-account-${account.id}`} value={String(account.id)}>
                  {account.name}
                </option>
              ))}
            </select>

            <label>Debut periode</label>
            <input
              type="date"
              value={invoiceFilters.dateFrom}
              onChange={(event) => setInvoiceFilters((previous) => ({ ...previous, dateFrom: event.target.value }))}
            />

            <label>Fin periode</label>
            <input
              type="date"
              value={invoiceFilters.dateTo}
              onChange={(event) => setInvoiceFilters((previous) => ({ ...previous, dateTo: event.target.value }))}
            />
          </div>

          <div className="master-control-actions">
            <button
              type="button"
              className="master-primary-button"
              onClick={handleGenerateSelfBillingInvoice}
              disabled={savingId === 'generate-self-billing'}
            >
              {savingId === 'generate-self-billing' ? 'Generation...' : 'Generer autofacture'}
            </button>
            <button
              type="button"
              className="master-secondary-button"
              onClick={handleRefreshInvoices}
              disabled={savingId === 'refresh-invoices'}
            >
              {savingId === 'refresh-invoices' ? 'Chargement...' : 'Rafraichir factures'}
            </button>
          </div>

          <div className="master-backup-list master-invoice-list">
            {selfBillingInvoices.length ? selfBillingInvoices.map((invoice) => (
              <article key={`invoice-${invoice.id}`} className="master-backup-item">
                <strong>{invoice.invoiceNumber}</strong>
                <span>
                  {new Date(invoice.periodStart).toLocaleDateString('fr-FR')}
                  {' au '}
                  {new Date(invoice.periodEnd).toLocaleDateString('fr-FR')}
                </span>
                <small>
                  Net restaurant: {formatCurrency(invoice.totals?.netPayableToRestaurant || 0)}
                  {' · '}
                  CB {formatCurrency(invoice.totals?.cardGrossTtc || 0)}
                  {' · '}
                  Especes {formatCurrency(invoice.totals?.cashDirectTtc || 0)}
                </small>
                <div className="master-backup-actions">
                  <button
                    type="button"
                    className="master-secondary-button"
                    onClick={() => handleDownloadSelfBillingInvoice(invoice.id)}
                    disabled={savingId === `download-invoice-${invoice.id}`}
                  >
                    {savingId === `download-invoice-${invoice.id}` ? 'Telechargement...' : 'Telecharger'}
                  </button>
                </div>
              </article>
            )) : (
              <p className="master-empty-text">Aucune autofacture pour cette periode.</p>
            )}
          </div>
        </section>

        <section className="master-create-card">
          <div>
            <span className="master-kicker">BELORA</span>
            <h2>Inventaire BEGuest</h2>
            <p className="master-empty-text">
              {beloraManagerReachable
                ? `Manager BEESP joignable${beloraManagerBaseUrl ? ` · ${beloraManagerBaseUrl}` : ''}`
                : 'Manager BEESP hors ligne ou non joignable depuis MASTER.'}
            </p>
            <div className="master-backup-actions">
              <button
                type="button"
                className="master-secondary-button"
                onClick={handleRefreshBeguestInventory}
                disabled={savingId === 'beguest-refresh'}
              >
                {savingId === 'beguest-refresh' ? 'Rafraichissement...' : 'Recalculer les etats'}
              </button>
            </div>
          </div>
          <div className="master-backup-list">
            {beloraBeguestCatalog.length ? (
              beloraBeguestCatalog.map((device) => {
                const syncMeta = getBeguestSyncMeta(device);
                return (
                  <article key={`beguest-${device.deviceName}`} className="master-backup-item">
                    <div className="master-account-topline">
                      <div>
                        <strong>{device.deviceName}</strong>
                        <span>
                          {device.assignedAccountName ? `Compte: ${device.assignedAccountName}` : 'Non assigne'}
                          {device.registeredInManager === false ? ' · absent du manager' : ''}
                        </span>
                      </div>
                      <span className={`master-status-pill ${syncMeta.className}`}>{syncMeta.label}</span>
                    </div>
                    <small>
                      {device.siteName ? `${device.siteName} · ` : ''}
                      {device.serialPort ? `${device.serialPort} · ` : ''}
                      {device.menuVersion ? `${device.menuVersion} · ` : ''}
                      {Number(device.productCount || 0)} produits
                      {' · '}
                      {Number(device.composedMenuCount || 0)} menus composes
                    </small>
                    <small>{syncMeta.detail}</small>
                    <div className="master-mini-stats">
                      <div>
                        <span>Version cible</span>
                        <strong>{device.desiredVersion || '-'}</strong>
                        <small>{device.desiredChecksum || '-'}</small>
                      </div>
                      <div>
                        <span>Version appliquee</span>
                        <strong>{device.appliedVersion || '-'}</strong>
                        <small>{device.appliedChecksum || '-'}</small>
                      </div>
                      <div>
                        <span>Dernier check-in</span>
                        <strong>{formatDateTime(device.lastSeenAt)}</strong>
                        <small>{device.gatewayId ? `gateway ${device.gatewayId}` : 'aucune gateway'}</small>
                      </div>
                      <div>
                        <span>Derniere application</span>
                        <strong>{formatDateTime(device.lastAppliedAt)}</strong>
                        <small>
                          {device.batteryPercent !== null && device.batteryPercent !== undefined
                            ? `batterie ${device.batteryPercent}%`
                            : 'batterie inconnue'}
                        </small>
                      </div>
                    </div>
                    {device.pendingSince ? (
                      <small>En attente depuis {formatDateTime(device.pendingSince)}</small>
                    ) : null}
                    <div className="master-backup-actions">
                      <button
                        type="button"
                        className="master-secondary-button"
                        onClick={() => handlePreviewBeguestMenu(device.deviceName)}
                        disabled={savingId === `beguest-preview:${device.deviceName}` || !device.assignedAccountId}
                      >
                        {savingId === `beguest-preview:${device.deviceName}` ? 'Chargement...' : 'Apercu menu'}
                      </button>
                    </div>
                  </article>
                );
              })
            ) : (
              <p className="master-empty-text">Aucun BEGuest remonte dans l inventaire.</p>
            )}
          </div>
          {beguestMenuPreview?.deviceName ? (
            <div className="master-ip-block">
              <div className="master-section-title">
                <span>Apercu {beguestMenuPreview.deviceName}</span>
                <strong>{beguestMenuPreview.accountName || 'Sans compte'}</strong>
              </div>
              <div className="master-mini-stats">
                <div><span>Version</span><strong>{beguestMenuPreview.menu?.menu_version || '-'}</strong><small>{beguestMenuPreview.menu?.checksum || '-'}</small></div>
                <div><span>Produits</span><strong>{Array.isArray(beguestMenuPreview.menu?.products) ? beguestMenuPreview.menu.products.length : 0}</strong><small>carte chargee</small></div>
                <div><span>Menus</span><strong>{Array.isArray(beguestMenuPreview.menu?.composed_menus) ? beguestMenuPreview.menu.composed_menus.length : 0}</strong><small>menus composes</small></div>
                <div><span>Categories</span><strong>{Array.isArray(beguestMenuPreview.menu?.categories) ? beguestMenuPreview.menu.categories.length : 0}</strong><small>{beguestMenuPreview.menu?.restaurant_id || '-'}</small></div>
              </div>
              {Array.isArray(beguestMenuPreview.menu?.categories) && beguestMenuPreview.menu.categories.length ? (
                <div className="master-ip-list">
                  {beguestMenuPreview.menu.categories.slice(0, 8).map((category) => (
                    <div key={`preview-category-${category.id}`} className="master-ip-chip">
                      <span>{category.name}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </section>

        <section className="master-create-card">
          <div>
            <span className="master-kicker">Secours</span>
            <h2>Sauvegardes SQLite</h2>
          </div>
          {backups.length ? (
            <div className="master-backup-list">
              {backups.slice(0, 6).map((backup) => (
                <article key={backup.filename} className="master-backup-item">
                  <strong>{backup.filename}</strong>
                  <span>{new Date(backup.createdAt).toLocaleString('fr-FR')}</span>
                  <small>
                    {backup.type === 'full' ? 'Complete' : 'SQLite'}
                    {' · '}
                    {Math.round(Number(backup.size || 0) / 1024)} KB
                    {backup.metadata?.reason ? ` · ${backup.metadata.reason}` : ''}
                    {backup.metadata?.fatal ? ' · fatal' : ''}
                  </small>
                  <div className="master-backup-actions">
                    <button
                      type="button"
                      className="master-secondary-button"
                      onClick={() => handleRestoreBackup(backup)}
                      disabled={savingId === `restore:${backup.filename}`}
                    >
                      {savingId === `restore:${backup.filename}` ? 'Restauration...' : 'Restaurer'}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <p className="master-empty-text">Aucune sauvegarde disponible pour le moment.</p>
          )}
        </section>

        <section className="master-create-card">
          <div>
            <span className="master-kicker">Nouveau compte</span>
            <h2>Ajouter une borne</h2>
          </div>
          <form onSubmit={handleCreateAccount} className="master-create-form">
            <input
              placeholder="Nom du compte"
              value={newAccountName}
              onChange={(event) => setNewAccountName(event.target.value)}
              required
            />
            <input
              type="password"
              placeholder="Mot de passe"
              value={newAccountPassword}
              onChange={(event) => setNewAccountPassword(event.target.value)}
              required
            />
            <select
              value={newAccountImageModel}
              onChange={(event) => setNewAccountImageModel(event.target.value)}
            >
              {imageModelOptions.map((model) => (
                <option key={`new-model-${model}`} value={model}>
                  {model}
                </option>
              ))}
            </select>
            <div className="master-account-form-heading">
              <strong>Modules et quotas</strong>
              <small>Définit ce que ce restaurant peut utiliser.</small>
            </div>
            {ACCOUNT_FEATURE_ROWS.map(([enabledField, limitField, label]) => (
              <div key={`new-${enabledField}`} className="master-feature-row">
                <label className="master-checkbox-row">
                  <input
                    type="checkbox"
                    checked={newAccountFeatures[enabledField] !== false}
                    onChange={(event) => setNewAccountFeatures((current) => ({ ...current, [enabledField]: event.target.checked }))}
                  />
                  <span>{label}</span>
                </label>
                {limitField ? (
                  <input
                    type="number"
                    min="0"
                    max="99"
                    value={newAccountFeatures[limitField]}
                    onChange={(event) => setNewAccountFeatures((current) => ({ ...current, [limitField]: event.target.value }))}
                  />
                ) : (
                  <span className="master-feature-note">Module web</span>
                )}
              </div>
            ))}
            <div className="master-form-grid master-form-grid-2">
              <label className="master-field">
                <span>Messages chatbot / client / jour</span>
                <input
                  type="number"
                  min="1"
                  max="500"
                  value={newAccountFeatures.customerChatbotDailyLimit}
                  onChange={(event) => setNewAccountFeatures((current) => ({
                    ...current,
                    customerChatbotDailyLimit: event.target.value,
                  }))}
                />
              </label>
              <label className="master-field">
                <span>Messages chatbot / IP / jour</span>
                <input
                  type="number"
                  min="1"
                  max="2000"
                  value={newAccountFeatures.customerChatbotIpDailyLimit}
                  onChange={(event) => setNewAccountFeatures((current) => ({
                    ...current,
                    customerChatbotIpDailyLimit: event.target.value,
                  }))}
                />
              </label>
            </div>
            <div className="master-account-form-heading">
              <strong>Services connectés</strong>
              <small>Crée les accès client sans supprimer les comptes existants.</small>
            </div>
            {SERVICE_ACCESS_ROWS.map(([serviceKey, label, description]) => (
              <label key={`new-service-${serviceKey}`} className="master-service-row">
                <input
                  type="checkbox"
                  checked={!!newAccountServices[serviceKey]}
                  onChange={(event) => setNewAccountServices((current) => ({ ...current, [serviceKey]: event.target.checked }))}
                />
                <span>
                  <strong>{label}</strong>
                  <small>{description}</small>
                </span>
              </label>
            ))}
            <button type="submit" className="master-primary-button" disabled={savingId === 'new'}>
              {savingId === 'new' ? 'Creation...' : 'Ajouter'}
            </button>
          </form>
        </section>

        <section className="master-account-grid">
          {accounts.map((account) => {
            const draft = draftByAccount[account.id] || {
              name: account.name,
              password: '',
              isActive: true,
              imageGenerationModel: account.imageGenerationModel || imageModelOptions[0],
              features: createFeaturesDraft(account.features),
              serviceAccess: createServiceAccessDraft(account.services),
              billing: createBillingDraft(account),
              uberDirect: createUberDirectDraft(account.uberDirect),
            };
            const billingDraft = draft.billing || createBillingDraft(account);
            return (
              <article key={account.id} className="master-account-card">
                <div className="master-account-topline">
                  <div>
                    <h3>{account.name}</h3>
                    <p>{account.isOnline ? 'Borne connectee' : 'Aucune borne connectee'}</p>
                  </div>
                  <span className={`master-status-pill ${account.isOnline ? 'is-online' : 'is-offline'}`}>
                    {account.isOnline ? 'En ligne' : 'Hors ligne'}
                  </span>
                </div>

                <div className="master-mini-stats">
                  <div><span>Jour</span><strong>{account.ordersToday} cmd</strong><small>{formatCurrency(account.revenueToday)}</small></div>
                  <div><span>Mois</span><strong>{account.ordersMonth} cmd</strong><small>{formatCurrency(account.revenueMonth)}</small></div>
                  <div><span>Delta CA</span><strong>{formatDelta(account.deltaRevenuePct)}</strong><small>vs mois precedent</small></div>
                  <div><span>SMS</span><strong>{account.smsSentMonth}</strong><small>{account.customersVerified} valides</small></div>
                </div>

                <div className="master-ip-block">
                  <div className="master-section-title">
                    <span>IP memorisees</span>
                    <strong>{(account.registeredIps || []).length}</strong>
                  </div>
                  {(account.registeredIps || []).length ? (
                    <div className="master-ip-list">
                      {account.registeredIps.map((ip) => (
                        <div key={ip} className="master-ip-chip">
                          <span>{ip}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveIp(account.id, ip)}
                            disabled={savingId === `${account.id}:${ip}`}
                          >
                            Exclure
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="master-empty-text">Aucune IP enregistree pour ce compte.</p>
                  )}
                </div>

                <div className="master-account-form">
                  <label>Nom du compte</label>
                  <input
                    value={draft.name}
                    onChange={(event) =>
                      setDraftByAccount((previous) => ({
                        ...previous,
                        [account.id]: { ...draft, name: event.target.value },
                      }))
                    }
                  />
                  <label>Nouveau mot de passe</label>
                  <input
                    type="password"
                    value={draft.password}
                    onChange={(event) =>
                      setDraftByAccount((previous) => ({
                        ...previous,
                        [account.id]: { ...draft, password: event.target.value },
                      }))
                    }
                  />
                  <label className="master-checkbox-row">
                    <input
                      type="checkbox"
                      checked={!!draft.isActive}
                      onChange={(event) =>
                        setDraftByAccount((previous) => ({
                          ...previous,
                          [account.id]: { ...draft, isActive: event.target.checked },
                        }))
                      }
                    />
                    <span>Compte actif</span>
                  </label>
                  <div className="master-account-form-heading">
                    <strong>Modules actifs</strong>
                    <small>Controle les interfaces et options visibles dans l'admin du client.</small>
                  </div>
                  <div className="master-account-form-heading">
                    <strong>Services connectés</strong>
                    <small>Active les accès BESTOCK, BEQUALITY, BECOST et BETIME liés à ce compte BEKIOSK.</small>
                  </div>
                  {SERVICE_ACCESS_ROWS.map(([serviceKey, label, description]) => {
                    const service = account.services?.[serviceKey] || {};
                    return (
                      <label key={`account-${account.id}-service-${serviceKey}`} className="master-service-row">
                        <input
                          type="checkbox"
                          checked={!!draft.serviceAccess?.[serviceKey]}
                          onChange={(event) =>
                            setDraftByAccount((previous) => ({
                              ...previous,
                              [account.id]: {
                                ...draft,
                                serviceAccess: {
                                  ...createServiceAccessDraft(draft.serviceAccess),
                                  [serviceKey]: event.target.checked,
                                },
                              },
                            }))
                          }
                        />
                        <span>
                          <strong>{label}</strong>
                          <small>{service.accessEmail || description}</small>
                          {service.provisioningStatus ? (
                            <em>{service.provisioningStatus}{service.provisioningMessage ? ` · ${service.provisioningMessage}` : ''}</em>
                          ) : null}
                        </span>
                      </label>
                    );
                  })}
                  <div className="master-database-pill">
                    <span>BDD resto</span>
                    <strong>{account.database?.key || 'à créer'}</strong>
                    <small>{account.database?.path || 'tenant-databases/...sqlite'}</small>
                    <button
                      type="button"
                      className="master-secondary-button master-tenant-sync-button"
                      onClick={() => handleSyncTenantDatabase(account.id)}
                      disabled={savingId === `tenant-db:${account.id}`}
                    >
                      {savingId === `tenant-db:${account.id}` ? 'Synchro...' : 'Synchroniser BDD resto'}
                    </button>
                  </div>
                  <div className="master-ip-block">
                    <div className="master-section-title">
                      <span>BEGuest assignés</span>
                      <strong>{(draft.beguestDevices || []).length}</strong>
                    </div>
                    <div className="master-form-grid master-form-grid-2">
                      <label className="master-field">
                        <span>Code admin BEGuest</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={draft.beguestSettings?.adminCode || '1993'}
                          onChange={(event) =>
                            setDraftByAccount((previous) => ({
                              ...previous,
                              [account.id]: {
                                ...draft,
                                beguestSettings: {
                                  ...createBeguestSettingsDraft(draft.beguestSettings),
                                  adminCode: event.target.value.replace(/\D+/g, '').slice(0, 8),
                                },
                              },
                            }))
                          }
                          placeholder="1993"
                        />
                      </label>
                      <label className="master-field">
                        <span>Numéro key account manager</span>
                        <input
                          type="text"
                          value={draft.beguestSettings?.supportPhone || ''}
                          onChange={(event) =>
                            setDraftByAccount((previous) => ({
                              ...previous,
                              [account.id]: {
                                ...draft,
                                beguestSettings: {
                                  ...createBeguestSettingsDraft(draft.beguestSettings),
                                  supportPhone: event.target.value,
                                },
                              },
                            }))
                          }
                          placeholder="+33 ..."
                        />
                      </label>
                      <label className="master-field">
                        <span>Logo resto BEGuest</span>
                        <input
                          type="text"
                          value={draft.beguestSettings?.restaurantLogoMedia || ''}
                          onChange={(event) =>
                            setDraftByAccount((previous) => ({
                              ...previous,
                              [account.id]: {
                                ...draft,
                                beguestSettings: {
                                  ...createBeguestSettingsDraft(draft.beguestSettings),
                                  restaurantLogoMedia: event.target.value,
                                },
                              },
                            }))
                          }
                          placeholder="URL ou media du logo resto"
                        />
                      </label>
                    </div>
                    <div className="master-discount-row">
                      <input
                        list={`beguest-catalog-${account.id}`}
                        value={draft.beguestInput || ''}
                        onChange={(event) =>
                          setDraftByAccount((previous) => ({
                            ...previous,
                            [account.id]: { ...draft, beguestInput: event.target.value },
                          }))
                        }
                        placeholder="BEGUEST01"
                      />
                      <button
                        type="button"
                        className="master-secondary-button"
                        onClick={() => addBeguestToAccountDraft(account.id)}
                      >
                        Ajouter
                      </button>
                      <datalist id={`beguest-catalog-${account.id}`}>
                        {beloraBeguestCatalog.map((device) => (
                          <option key={`beguest-option-${account.id}-${device.deviceName}`} value={device.deviceName}>
                            {device.assignedAccountName ? `${device.assignedAccountName}` : 'Disponible'}
                          </option>
                        ))}
                      </datalist>
                    </div>
                    {(draft.beguestDevices || []).length ? (
                      <div className="master-ip-list">
                        {(draft.beguestDevices || []).map((deviceName) => (
                          <div key={`account-${account.id}-beguest-${deviceName}`} className="master-ip-chip">
                            <span>{deviceName}</span>
                            <button type="button" onClick={() => removeBeguestFromAccountDraft(account.id, deviceName)}>
                              Retirer
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="master-empty-text">Aucun terminal BEGuest assigné.</p>
                    )}
                  </div>
                  {ACCOUNT_FEATURE_ROWS.map(([enabledField, limitField, label]) => (
                    <div key={`account-${account.id}-${enabledField}`} className="master-feature-row">
                      <label className="master-checkbox-row">
                        <input
                          type="checkbox"
                          checked={draft.features?.[enabledField] !== false}
                          onChange={(event) =>
                            setDraftByAccount((previous) => ({
                              ...previous,
                              [account.id]: {
                                ...draft,
                                features: {
                                  ...(draft.features || {}),
                                  [enabledField]: event.target.checked,
                                },
                              },
                            }))
                          }
                        />
                        <span>{label}</span>
                      </label>
                      {limitField ? (
                        <input
                          type="number"
                          min="0"
                          max="99"
                          value={draft.features?.[limitField] ?? 0}
                          onChange={(event) =>
                            setDraftByAccount((previous) => ({
                              ...previous,
                              [account.id]: {
                                ...draft,
                                features: {
                                  ...(draft.features || {}),
                                  [limitField]: event.target.value,
                                },
                              },
                            }))
                          }
                        />
                      ) : (
                        <span className="master-feature-note">Module web</span>
                      )}
                    </div>
                  ))}
                  <div className="master-form-grid master-form-grid-2">
                    <label className="master-field">
                      <span>Messages chatbot / client / jour</span>
                      <input
                        type="number"
                        min="1"
                        max="500"
                        value={draft.features?.customerChatbotDailyLimit ?? 20}
                        onChange={(event) =>
                          setDraftByAccount((previous) => ({
                            ...previous,
                            [account.id]: {
                              ...draft,
                              features: {
                                ...(draft.features || {}),
                                customerChatbotDailyLimit: event.target.value,
                              },
                            },
                          }))
                        }
                      />
                    </label>
                    <label className="master-field">
                      <span>Messages chatbot / IP / jour</span>
                      <input
                        type="number"
                        min="1"
                        max="2000"
                        value={draft.features?.customerChatbotIpDailyLimit ?? 80}
                        onChange={(event) =>
                          setDraftByAccount((previous) => ({
                            ...previous,
                            [account.id]: {
                              ...draft,
                              features: {
                                ...(draft.features || {}),
                                customerChatbotIpDailyLimit: event.target.value,
                              },
                            },
                          }))
                        }
                      />
                    </label>
                  </div>
                  <label>Modele image IA</label>
                  <select
                    value={draft.imageGenerationModel || imageModelOptions[0]}
                    onChange={(event) =>
                      setDraftByAccount((previous) => ({
                        ...previous,
                        [account.id]: { ...draft, imageGenerationModel: event.target.value },
                      }))
                    }
                  >
                    {imageModelOptions.map((model) => (
                      <option key={`account-${account.id}-model-${model}`} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>

                  <div className="master-account-form-heading">
                    <strong>Facturation restaurant</strong>
                    <small>Infos legales pour autofactures et dossier comptable.</small>
                  </div>

                  <label>Raison sociale</label>
                  <input
                    value={billingDraft.legalName || ''}
                    onChange={(event) => updateAccountBillingDraft(account.id, 'legalName', event.target.value)}
                    placeholder="Ex: THAI CHU"
                  />
                  <label>Enseigne</label>
                  <input
                    value={billingDraft.tradeName || ''}
                    onChange={(event) => updateAccountBillingDraft(account.id, 'tradeName', event.target.value)}
                    placeholder="Ex: MAYTHAI"
                  />
                  <label>Adresse</label>
                  <input
                    value={billingDraft.billingAddressLine1 || ''}
                    onChange={(event) => updateAccountBillingDraft(account.id, 'billingAddressLine1', event.target.value)}
                  />
                  <label>Complement adresse</label>
                  <input
                    value={billingDraft.billingAddressLine2 || ''}
                    onChange={(event) => updateAccountBillingDraft(account.id, 'billingAddressLine2', event.target.value)}
                  />
                  <label>Code postal</label>
                  <input
                    value={billingDraft.billingPostalCode || ''}
                    onChange={(event) => updateAccountBillingDraft(account.id, 'billingPostalCode', event.target.value)}
                  />
                  <label>Ville</label>
                  <input
                    value={billingDraft.billingCity || ''}
                    onChange={(event) => updateAccountBillingDraft(account.id, 'billingCity', event.target.value)}
                  />
                  <label>SIRET</label>
                  <input
                    value={billingDraft.siret || ''}
                    onChange={(event) => updateAccountBillingDraft(account.id, 'siret', event.target.value)}
                  />
                  <label>TVA intracom</label>
                  <input
                    value={billingDraft.vatNumber || ''}
                    onChange={(event) => updateAccountBillingDraft(account.id, 'vatNumber', event.target.value)}
                  />
                  <label>NAF / APE</label>
                  <input
                    value={billingDraft.nafCode || ''}
                    onChange={(event) => updateAccountBillingDraft(account.id, 'nafCode', event.target.value)}
                  />
                  <label>Abonnement HT mensuel</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={billingDraft.subscriptionFeeHt}
                    onChange={(event) => updateAccountBillingDraft(account.id, 'subscriptionFeeHt', event.target.value)}
                  />
                  <label>Remise abonnement %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={billingDraft.subscriptionDiscountPct}
                    onChange={(event) => updateAccountBillingDraft(account.id, 'subscriptionDiscountPct', event.target.value)}
                  />

                </div>

                <div className="master-account-form-heading">
                  <strong>Uber Direct — livraison BECLICK</strong>
                  <small>Les frais de livraison sont intégralement ajoutés au total payé par le client.</small>
                </div>
                {draft.uberDirect?.integrationMode === 'partner' ? <p className="master-empty-text">
                  Cet établissement utilise le parcours partenaire BEQUANIM, avec facturation directe par Uber.
                  {' '}<a href="#uber-direct-overview-title">Gérer sa connexion et son activation dans la vue Uber Direct ci-dessus.</a>
                </p> : <>
                <label className="master-checkbox-row">
                  <input
                    type="checkbox"
                    checked={!!draft.uberDirect?.enabled}
                    onChange={(event) => updateAccountUberDirectDraft(account.id, 'enabled', event.target.checked)}
                  />
                  <span>Activer la livraison Uber Direct</span>
                </label>
                <div className="master-form-grid master-form-grid-2">
                  <label className="master-field"><span>Environnement</span><select value={draft.uberDirect?.environment || 'sandbox'} onChange={(event) => updateAccountUberDirectDraft(account.id, 'environment', event.target.value)}><option value="sandbox">Test / sandbox</option><option value="production">Production</option></select></label>
                  <label className="master-field"><span>Customer ID</span><input value={draft.uberDirect?.customerId || ''} onChange={(event) => updateAccountUberDirectDraft(account.id, 'customerId', event.target.value)} placeholder="UUID Uber Direct" /></label>
                  <label className="master-field"><span>Client ID</span><input value={draft.uberDirect?.clientId || ''} onChange={(event) => updateAccountUberDirectDraft(account.id, 'clientId', event.target.value)} /></label>
                  <label className="master-field"><span>Client Secret {draft.uberDirect?.clientSecretConfigured ? '(déjà enregistré)' : ''}</span><input type="password" value={draft.uberDirect?.clientSecret || ''} onChange={(event) => updateAccountUberDirectDraft(account.id, 'clientSecret', event.target.value)} placeholder={draft.uberDirect?.clientSecretConfigured ? 'Laisser vide pour conserver' : 'À renseigner'} /></label>
                  <label className="master-field"><span>Clé de signature webhook {draft.uberDirect?.webhookSigningKeyConfigured ? '(enregistrée)' : ''}</span><input type="password" value={draft.uberDirect?.webhookSigningKey || ''} onChange={(event) => updateAccountUberDirectDraft(account.id, 'webhookSigningKey', event.target.value)} placeholder="Fournie dans le dashboard Uber" /></label>
                  <label className="master-field"><span>Nom du point de retrait</span><input value={draft.uberDirect?.pickupName || ''} onChange={(event) => updateAccountUberDirectDraft(account.id, 'pickupName', event.target.value)} /></label>
                  <label className="master-field"><span>Téléphone restaurant</span><input value={draft.uberDirect?.pickupPhone || ''} onChange={(event) => updateAccountUberDirectDraft(account.id, 'pickupPhone', event.target.value)} /></label>
                  <label className="master-field"><span>Adresse</span><input value={draft.uberDirect?.pickupAddressLine1 || ''} onChange={(event) => updateAccountUberDirectDraft(account.id, 'pickupAddressLine1', event.target.value)} /></label>
                  <label className="master-field"><span>Complément</span><input value={draft.uberDirect?.pickupAddressLine2 || ''} onChange={(event) => updateAccountUberDirectDraft(account.id, 'pickupAddressLine2', event.target.value)} /></label>
                  <label className="master-field"><span>Code postal</span><input value={draft.uberDirect?.pickupPostalCode || ''} onChange={(event) => updateAccountUberDirectDraft(account.id, 'pickupPostalCode', event.target.value)} /></label>
                  <label className="master-field"><span>Ville</span><input value={draft.uberDirect?.pickupCity || ''} onChange={(event) => updateAccountUberDirectDraft(account.id, 'pickupCity', event.target.value)} /></label>
                  <label className="master-field"><span>Pays</span><input value={draft.uberDirect?.pickupCountry || 'FR'} maxLength="2" onChange={(event) => updateAccountUberDirectDraft(account.id, 'pickupCountry', event.target.value.toUpperCase())} /></label>
                  <label className="master-field"><span>Latitude (optionnel)</span><input type="number" step="any" value={draft.uberDirect?.pickupLatitude ?? ''} onChange={(event) => updateAccountUberDirectDraft(account.id, 'pickupLatitude', event.target.value)} /></label>
                  <label className="master-field"><span>Longitude (optionnel)</span><input type="number" step="any" value={draft.uberDirect?.pickupLongitude ?? ''} onChange={(event) => updateAccountUberDirectDraft(account.id, 'pickupLongitude', event.target.value)} /></label>
                </div>
                <p className="master-empty-text">
                  {draft.uberDirect?.configured ? 'Configuration API complète.' : `Configuration en attente${draft.uberDirect?.missingFields?.length ? ` : ${draft.uberDirect.missingFields.join(', ')}` : ''}.`}
                  {' '}Webhook : https://beborne.com/api/uber-direct/webhook
                </p>
                </>}

                <div className="master-account-actions">
                  <button
                    type="button"
                    className="master-primary-button"
                    onClick={() => handleUpdateAccount(account.id)}
                    disabled={savingId === account.id}
                  >
                    {savingId === account.id ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                  <button
                    type="button"
                    className="master-secondary-button"
                    onClick={() => handleDisconnectAccount(account.id)}
                    disabled={savingId === `disconnect:${account.id}`}
                  >
                    {savingId === `disconnect:${account.id}` ? 'Deconnexion...' : 'Deconnecter les bornes'}
                  </button>
                </div>
              </article>
            );
          })}
        </section>
      </div>
    </div>
  );
}

export default MasterConsole;
